// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import FilterControls, { type FilterValue } from 'main-app/components/FilterControls';
import type { RouterHistory } from 'react-router';
import getSortOptions from './getSortOptions';
import getFilterOptions from './getFilterOptions';
import PaginatedBackordersContainer from './PaginatedBackordersContainer';

type Props = { history: RouterHistory };

type State = {
  search: string,
  graphqlSearchValue: string,
};

const DEFAULT_FILTER = {
  sort: 'CREATED_AT',
  direction: 'DESC',
};

class Backorders extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      search: '',
      graphqlSearchValue: '',
    };
    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;
    if (!search) return DEFAULT_FILTER;
    return { ...DEFAULT_FILTER, ...qs.parse(search, { ignoreQueryPrefix: true }) };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;
    history.replace(
      '/backorders?' + qs.stringify(filters, { encode: true, arrayFormat: 'brackets' }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;
    return !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) || graphqlSearchValue;
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;
    const queryVariables = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: { field: filters.sort, direction: filters.direction },
    };

    delete queryVariables.sort;
    delete queryVariables.direction;
    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState({ search }, this.handleDebouncedSearch);
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;
    this.setState({ graphqlSearchValue: search });
    Analytics.trackEvent('Search Applied', {
      listName: 'Backorders',
      searchString: search,
    });
  };

  render() {
    const { history } = this.props;
    const { search } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query BackordersQuery(
            $count: Int!
            $cursor: String
            $search: String
            $sortBy: BackorderSort
            $statuses: [BackorderStatus!]
            $customerIds: [ID!]
            $orderIds: [ID!]
          ) {
            ...PaginatedBackordersContainer_backorders
              @arguments(
                search: $search
                sortBy: $sortBy
                statuses: $statuses
                customerIds: $customerIds
                orderIds: $orderIds
              )
            viewer {
              id
              permissions
              company {
                id
              }
            }
          }
        `}
        variables={{ ...this.compileQueryVariables(), count: 30, cursor: null }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Backorders')}
              searchPlaceholder={i18n.t('Customer, Order #, or Backorder #')}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              savedFilterType="BACKORDER"
              onFiltersChange={filters => this.setFilters(filters)}
            />
            {!props ? (
              <Loader />
            ) : (
              <PaginatedBackordersContainer
                history={history}
                filtersDirty={this.filtersDirty()}
                onCreateNew={() => {}}
                backorders={props}
              />
            )}
          </Page>
        )}
      />
    );
  }
}

export default Backorders;
